import { useCallback, useEffect, useState } from "react";
import type { Me } from "../shared/types";
import { api, ApiError } from "./api";
import { initIdentity, logout, type IdentityUser } from "./auth";
import { useRoute } from "./router";
import { Login } from "./components/Login";
import { AppBar } from "./components/AppBar";
import { PatientList } from "./components/PatientList";
import { RecordView } from "./components/record/RecordView";
import { EmergencyView } from "./components/EmergencyView";
import { ScanApp } from "./components/scan/ScanApp";

type Session =
  | { state: "loading" }
  | { state: "signed-out" }
  | { state: "no-role"; email: string; message: string }
  | { state: "error"; message: string }
  | { state: "ready"; me: Me };

export function App() {
  const [session, setSession] = useState<Session>({ state: "loading" });
  const route = useRoute();

  const onIdentity = useCallback(async (user: IdentityUser | null) => {
    if (!user) return setSession({ state: "signed-out" });
    setSession({ state: "loading" });
    try {
      setSession({ state: "ready", me: await api.me() });
    } catch (e) {
      const err = e as ApiError;
      if (err.status === 403) setSession({ state: "no-role", email: user.email, message: err.message });
      else if (err.status === 401) setSession({ state: "signed-out" });
      else setSession({ state: "error", message: err.message });
    }
  }, []);

  useEffect(() => initIdentity(onIdentity), [onIdentity]);

  if (session.state === "loading") return <div className="center"><div className="spinner" />Loading Vetproof…</div>;
  if (session.state === "signed-out") return <Login />;
  if (session.state !== "ready") {
    return (
      <div className="center" style={{ maxWidth: 460, margin: "0 auto", textAlign: "center" }}>
        <img src="/vetproof-logo.png" alt="Vetproof" style={{ height: 40 }} />
        <div className="err">{session.message}</div>
        <button className="btn ghost" onClick={logout}>Sign out</button>
      </div>
    );
  }

  const { me } = session;
  return (
    <>
      <AppBar me={me} route={route.name} />
      {route.name === "list" && <PatientList me={me} />}
      {route.name === "pet" && <RecordView key={route.id} me={me} petId={route.id} initialTab={route.tab} />}
      {route.name === "emergency" && <EmergencyView me={me} chip={route.chip} />}
      {route.name === "scan" && <ScanApp me={me} initialChip={route.chip} />}
    </>
  );
}
